// Screen that replaces the subtree that failed.
//
// DELIBERATELY without MUI and without theme tokens, styles inline: this UI renders exactly when
// something already broke, and if it depended on a provider (theme, i18n, query) it could fail for
// the SAME reason as the original error and blank the screen all over again. The blue comes from
// `config/palette.ts`, which is a leaf module of constants and not a provider: importing it cannot
// fail the way a theme or a catalogue can, and it keeps this screen the same blue as the rest of
// the app instead of holding a copy of the hex.
//
// The user-facing copy stays in Spanish, like the rest of the app and like carto2 (which had no
// i18n at all: 0 deps, 0 catalogues, literals in the templates). See the module README.
import { CONSORCIO_BLUE } from '@/config/palette'
import { DEBUG } from '@/config/settings'

interface Props {
  /** The captured error, only used to show its message in debug mode. */
  error: Error
  /** Renders the subtree again (exposed by the boundary). */
  onReset: () => void
  /** `true` when the boundary covers a piece of the page rather than the whole page. */
  embedded?: boolean
}

export function ErrorFallback({ error, onReset, embedded = false }: Props) {
  return (
    <div
      role='alert'
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        padding: 24,
        textAlign: 'center',
        height: embedded ? '100%' : '100vh',
        width: '100%',
        boxSizing: 'border-box',
        fontFamily: "'Inter Variable', Inter, system-ui, sans-serif",
        color: '#1a1e29',
        background: '#ffffff',
      }}
    >
      <h2 style={{ margin: 0, fontSize: 20, color: CONSORCIO_BLUE }}>
        {embedded ? 'No se ha podido cargar el mapa' : 'Algo ha fallado'}
      </h2>

      <p style={{ margin: 0, maxWidth: 460, lineHeight: 1.5 }}>
        {embedded
          ? 'El resto de la herramienta sigue funcionando: puedes seguir usando el formulario.'
          : 'Se ha producido un error inesperado y la pantalla no ha podido dibujarse.'}
      </p>

      {DEBUG && (
        <pre
          style={{
            margin: 0,
            maxWidth: '100%',
            overflow: 'auto',
            fontSize: 12,
            textAlign: 'left',
            padding: 8,
            background: '#f5f5f7',
            borderRadius: 4,
          }}
        >
          {error.message}
        </pre>
      )}

      <div
        style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
      >
        <button type='button' onClick={onReset} style={primaryButton}>
          Reintentar
        </button>
        {!embedded && (
          <button
            type='button'
            onClick={() => window.location.reload()}
            style={secondaryButton}
          >
            Recargar la página
          </button>
        )}
      </div>
    </div>
  )
}

const buttonBase = {
  padding: '8px 16px',
  borderRadius: 4,
  fontSize: 14,
  cursor: 'pointer',
} as const

const primaryButton = {
  ...buttonBase,
  border: `1px solid ${CONSORCIO_BLUE}`,
  background: CONSORCIO_BLUE,
  color: '#ffffff',
} as const

const secondaryButton = {
  ...buttonBase,
  border: `1px solid ${CONSORCIO_BLUE}`,
  background: 'transparent',
  color: CONSORCIO_BLUE,
} as const
